/**
 * SA-981 (design note §2): what the Inbox viewer shows in place.
 *
 * `viewableKind` is the twin of backend `media_storage.viewable_kind`, pinned
 * by shared/media-viewable-cases.json. The backend reads the stored type
 * through `served_mime_type` first. All four kinds are types the media route
 * serves as themselves, so the served type of each is its own bare type
 * (SA-715 Ruling T). The twin keeps its own copy of the raster list,
 * `RASTER_IMAGE_MIMES`, which shared/media-viewable-cases.json pins against
 * the backend's.
 *
 * `canShow` is a browser fact and lives only here. A kind the browser cannot
 * show is a download.
 */
export type ViewableKind = 'pdf' | 'image' | 'video' | 'audio'

const RASTER_IMAGE_MIMES = new Set(['image/jpeg', 'image/png', 'image/gif', 'image/webp'])

/** `Type/Sub; param=x` → `type/sub` (empty string for nothing). */
export function bareMimeType(mime: string | null | undefined): string {
  return (mime ?? '').split(';')[0].trim().toLowerCase()
}

export function viewableKind(mime: string | null | undefined): ViewableKind | null {
  const bare = bareMimeType(mime)
  if (bare === 'application/pdf') return 'pdf'
  if (RASTER_IMAGE_MIMES.has(bare)) return 'image'
  if (bare.startsWith('video/')) return 'video'
  if (bare.startsWith('audio/')) return 'audio'
  return null
}

export interface MediaEnv {
  /** `navigator.pdfViewerEnabled`; undefined in a browser that does not report it. */
  pdfViewerEnabled: boolean | undefined
  canPlayType: (element: 'video' | 'audio', mime: string) => string
}

export function browserEnv(): MediaEnv {
  return {
    pdfViewerEnabled: (navigator as Navigator & { pdfViewerEnabled?: boolean }).pdfViewerEnabled,
    canPlayType: (element, mime) => document.createElement(element).canPlayType(mime),
  }
}

/** Whether this browser can show `kind`: a PDF unless it reports no PDF
 * viewer (Android Chrome), an image always, a video always (SA-1094 M2: the
 * player decides; Chrome answers '' for video/quicktime and then plays most
 * .mov files, and a file it cannot decode fails on `error`), audio when
 * `canPlayType` answers anything. */
export function canShow(
  kind: ViewableKind,
  mime: string | null | undefined,
  env: MediaEnv = browserEnv(),
): boolean {
  if (kind === 'pdf') return env.pdfViewerEnabled !== false
  if (kind === 'image' || kind === 'video') return true
  return env.canPlayType('audio', bareMimeType(mime)) !== ''
}
